import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { fileToDataUrl } from '@/lib/imageDownscale'
import { toastHelpers } from '@/lib/toast-helpers'

/** Receipts need more detail than a product tile: small print must stay legible. */
const RECEIPT_MAX_EDGE = 1600

/** Splits a data: URL into what POST /receipt-scans takes. */
export function dataUrlParts(dataUrl: string): { content_type: string; data_base64: string } | null {
  const m = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl)
  return m ? { content_type: m[1], data_base64: m[2] } : null
}

/**
 * Scan receipt (F-RPT-N43). No `capture` attribute on purpose: a phone then
 * offers both "Take photo" and the photo library / files, and a computer
 * opens the file picker.
 *
 * Several photos at once (F-FE-N192, the end-of-week pile): each is shrunk
 * and uploaded in turn, and the reader works through them in the background.
 * One photo opens the review sheet; a pile goes to the inbox.
 */
export function ReceiptScanButton({
  onScanned,
  onBatch,
  variant = 'outline',
  label = 'Scan receipt',
}: {
  onScanned: (scanId: string) => void
  /** Called instead of onScanned when more than one photo was picked. */
  onBatch?: (scanIds: string[]) => void
  variant?: 'outline' | 'default'
  label?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const qc = useQueryClient()

  const uploadOne = async (file: File): Promise<string> => {
    const parts = dataUrlParts(await fileToDataUrl(file, RECEIPT_MAX_EDGE))
    if (!parts) throw new Error('That file is not a photo.')
    const res = await apiClient.uploadReceiptScan(parts)
    if (!res.data?.id) throw new Error('The receipt was not saved.')
    return res.data.id
  }

  const upload = async (files: File[]) => {
    setProgress({ done: 0, total: files.length })
    const ids: string[] = []
    const failed: string[] = []
    // One at a time: a venue uplink is slow, and the reader queues them anyway.
    for (const f of files) {
      try {
        ids.push(await uploadOne(f))
      } catch (e) {
        failed.push(f.name)
        if (files.length === 1) toastHelpers.apiError('Scan receipt', e)
      }
      setProgress(p => (p ? { ...p, done: p.done + 1 } : p))
    }
    setProgress(null)
    if (input.current) input.current.value = ''
    qc.invalidateQueries({ queryKey: ['receiptScans'] })
    qc.invalidateQueries({ queryKey: ['receiptScanSummary'] })
    if (files.length > 1) {
      if (failed.length) toastHelpers.warning(`${failed.length} of ${files.length} photos didn't upload`, failed.join(', '))
      if (ids.length) {
        toastHelpers.success(`${ids.length} ${ids.length === 1 ? 'receipt' : 'receipts'} uploaded`, 'They are being read now.')
        ;(onBatch ?? (list => onScanned(list[0])))(ids)
      }
    } else if (ids[0]) {
      onScanned(ids[0])
    }
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={e => {
          const files = Array.from(e.target.files ?? []).slice(0, 50)
          if (files.length) void upload(files)
        }}
      />
      <Button type="button" variant={variant} onClick={() => input.current?.click()} disabled={!!progress}>
        {progress ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}
        {progress && progress.total > 1 ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : label}
      </Button>
    </>
  )
}
